import {
  type PublicMessage,
  dismissStore,
  localStateKeys,
} from "appsShared/inAppMessages"

/**
 * Browser state of spec §Consumer local state. Every access is wrapped: a storage failure
 * (private mode, quota, a blocked iframe) means "not dismissed, not seen".
 */
const keys = localStateKeys("sl2")

function readMap(storage: () => Storage, key: string): Record<string, string> {
  try {
    const parsed = JSON.parse(storage().getItem(key) ?? "{}")
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return {}
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === "string"),
    ) as Record<string, string>
  } catch {
    return {}
  }
}

function writeEntry(storage: () => Storage, key: string, id: string, at: string) {
  try {
    const map = readMap(storage, key)
    if (map[id]) return
    map[id] = at
    storage().setItem(key, JSON.stringify(map))
  } catch {
    // Not stored: the message shows again next visit.
  }
}

const local = () => window.localStorage
const session = () => window.sessionStorage

/** Dismissals from both stores, merged, and the seen map. */
export function readInAppLocalState() {
  return {
    dismissed: {
      ...readMap(local, keys.dismissed),
      ...readMap(session, keys.dismissed),
    },
    seen: readMap(local, keys.seen),
  }
}

/** Writes the dismissal to the store its policy names. Returns false for `dismiss: none`. */
export function storeDismissal(message: PublicMessage, at: string): boolean {
  const store = dismissStore(message.dismiss)
  if (!store) return false
  writeEntry(store === "persistent" ? local : session, keys.dismissed, message.id, at)
  return true
}

/** First-seen marks; an existing mark is kept (`firstSeenAt`). */
export function storeSeen(ids: string[], at: string) {
  ids.forEach((id) => writeEntry(local, keys.seen, id, at))
}
